import {Box, FormContext, HStack, isReactElement, TabGroup} from "@hosttools/frontend";
import type {ReactElement} from "react";
import React, {useContext, useEffect, useMemo, useState} from "react";

import ScrollableParent from "./ScrollableParent";
import type {Props as TabProps} from "./Tab";
import Tab from "./Tab";
import TabButton from "./TabButton";
import {TableContext} from "./TabContext";
import type {TabVariant} from "./type";

export interface Props {
    defaultTab: string;
    children: Array<ReactElement<TabProps> | boolean | null | undefined>;
    size?: "sm" | "md";
    variant?: TabVariant;
    withForm?: boolean;
    className?: string;
    contentClassName?: string;
    onChange?: (tab: string) => void;
}

const Tabs: React.FC<Props> = ({
    defaultTab,
    variant = "buttonGroup",
    children,
    size = "md",
    withForm,
    className,
    contentClassName,
    onChange
}) => {
    const formContext = useContext(FormContext);
    const [activeTab, setActiveTab] = useState<string>(defaultTab);
    const childrenInArray = React.Children.toArray(children);

    useEffect(() => {
        if (!activeTab && !defaultTab && childrenInArray.length) {
            const firstChild = childrenInArray[0];
            if (isReactElement<TabProps>(firstChild, Tab)) {
                setActiveTab(firstChild.props.id);
            }
        }
    }, [activeTab, childrenInArray, defaultTab]);

    useEffect(() => {
        if (formContext && withForm) {
            formContext.setNavigateToGroup((group: string) => {
                setActiveTab(group);
            });
        }
    }, [formContext, withForm]);

    useEffect(() => {
        onChange?.(activeTab);
    }, [activeTab, onChange]);

    const tabsNode = childrenInArray
        .map(child => {
            if (isReactElement<TabProps>(child, Tab)) {
                return (
                    <TabButton
                        key={child.props.id}
                        {...child.props}
                        isActive={child.props.id === activeTab}
                        variant={variant}
                        onClick={setActiveTab}
                    />
                );
            }
            return undefined;
        })
        .filter(Boolean);

    return (
        <TableContext.Provider value={useMemo(() => ({withForm}), [withForm])}>
            <Box className={className}>
                {variant === "tab" ? (
                    <ScrollableParent>
                        <HStack space="2xl">{tabsNode}</HStack>
                    </ScrollableParent>
                ) : (
                    <TabGroup activeTab={activeTab} size={size}>
                        {tabsNode}
                    </TabGroup>
                )}
            </Box>
            <Box className={contentClassName}>
                {childrenInArray.map(child => {
                    if (isReactElement<TabProps>(child, Tab)) {
                        if (child.props.id === activeTab) {
                            return <Box key={child.props.id}>{child}</Box>;
                        }
                        return (
                            <Box key={child.props.id} className="h-0 overflow-hidden opacity-0">
                                {child}
                            </Box>
                        );
                    }
                    return null;
                })}
            </Box>
        </TableContext.Provider>
    );
};

export default Tabs;
